import React, { useState, useEffect, useRef } from 'react';
import idea from '../../Assets/Idea.svg'
import design from '../../Assets/Design.svg'
import develop from '../../Assets/develop.svg'
import test from '../../Assets/test.svg'
import launch from '../../Assets/launch.svg'
import support from '../../Assets/Support.svg'

const ProductDevelopmentProcess = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const leftColRef = useRef(null);
  const photosRef = useRef([]);
  const progressBarRef = useRef(null);

  const processSteps = [
    {
      id: 1,
      title: "Ideate",
      description: "We analyze your vision thoroughly to ensure the roadmap is perfectly aligned with your end goals, setting the stage for product success.",
      image: idea
    },
    {
      id: 2,
      title: "Design",
      description: "Crafting a minimal viable product (MVP) that balances design with core functionality, maximizing value and user satisfaction.",
      image: design
    },
    {
      id: 3,
      title: "Develop",
      description: "Developing end-to-end solutions with a focus on feasibility assessment, architecture design, and agile process to ensure rapid, high-quality delivery.",
      image: develop
    },
    {
      id: 4,
      title: "Test",
      description: "Ensuring your product meets the highest standards of quality and reliability through extensive QA and software testing across all user touch points.",
      image: test
    },
    {
      id: 5,
      title: "Launch",
      description: "Executing a successful product launch by developing tailored deployment plans, executing a smooth rollout, and offering dedicated post-launch assistance.",
      image: launch
    },
    {
      id: 6,
      title: "Support",
      description: "Providing ongoing support and enhancements to ensure continued product success.",
      image: support
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (!leftColRef.current) return;

      const detailsElements = leftColRef.current.querySelectorAll('.details');
      const windowHeight = window.innerHeight;
      const scrollPosition = window.scrollY;

      detailsElements.forEach((detail, index) => {
        const rect = detail.getBoundingClientRect();
        const elementTop = rect.top + scrollPosition;
        const elementBottom = rect.bottom + scrollPosition;

        // Check if element is in view
        if (scrollPosition + windowHeight * 0.3 >= elementTop && 
            scrollPosition + windowHeight * 0.3 <= elementBottom) {
          setActiveIndex(index);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Update photo positions based on active index
    if (photosRef.current.length > 0) {
      photosRef.current.forEach((photo, index) => {
        if (photo) {
          photo.style.transform = index === activeIndex 
            ? 'translate(0px, 0px)' 
            : 'translate(0px, 900px)';
        }
      });
    }

    // Update progress bar
    if (progressBarRef.current) {
      const progress = (activeIndex / (processSteps.length - 1)) * 100;
      progressBarRef.current.style.width = `${progress}%`;
    }
  }, [activeIndex, processSteps.length]);

  return (
    <section className="product bg-dark-theme" id="product">
      <div className="py-10">
        <div className="px-2 lg:px-8 max-w-7xl mx-auto">
          <div className="mb-20 flex justify-between xs:items-end xs:flex-row flex-col xs:gap-0 gap-6">
            <h2 className="text-3xl sm:text-3xl md:text-4xl font-bold text-center mb-2">
          <span className="relative inline-block bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
              Our Product Development Process 
          </span>
        </h2>
          </div>

          {/* Mobile View */}
          <div className="w-auto whitespace-nowrap overflow-auto block lg:hidden">
            {processSteps.map((step, index) => (
              <div key={step.id} className="mr-20 pb-4 sm:pb-24 xl:pb-0 inline-block w-full sm:w-[70%] md:w-[50%]">
                <div className="flex flex-col gap-x-8 gap-y-10 sm:gap-y-10 md:gap-y-8 xl:flex-row xl:items-stretch">
                  <img className="object-cover rounded-full" width="300" src={step.image} alt={step.title} />
                  <div className="whitespace-break-spaces">
                    <b className="font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 text-theme text-4xl xl:text-5xl 2xl:text-6xl tracking-[-2px]">{step.title}</b>
                    <p className="my-10 text-white text-sm lg:text-md xl:text-lg 2xl:text-xl">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop View */}
          <div className="gallery relative hidden lg:flex">
            <div className="left-col w-2/4" ref={leftColRef}>
              <div className="detailsWrapper pl-12">
                {processSteps.map((step, index) => (
                  <div key={step.id} className="details pb-36">
                    <div className="lg:pr-8 lg:pt-4">
                      <div className="lg:max-w-lg">
                        {/* <p className="text-white font-normal mb-3 text-sm lg:text-md xl:text-lg 2xl:text-xl">{`0${index + 1}/06`}</p> */}
                        <div className="overflow-hidden">
                          <b className="font-bold text-white text-4xl xl:text-5xl 2xl:text-6xl tracking-[-2px] lg:-translate-y-[7px] xl:!leading-[60px] 2xl:!leading-[75px]">
                            {step.title}
                          </b>
                        </div>
                        <p className="mt-7 mb-10 leading-9 text-white font-normal text-sm lg:text-md xl:text-lg 2xl:text-xl">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="right h-[34rem] w-96 ml-20 rounded-full overflow-hidden md:sticky top-[25%]">
              <div className="rounded-full">
                <div className="photos relative">
                  {processSteps.map((step, index) => (
                    <div 
                      key={step.id} 
                      className="photo w-full h-full absolute inset-0 transition-transform ease-out duration-300"
                      ref={el => photosRef.current[index] = el}
                      style={{ transform: index === activeIndex ? 'translate(0px, 0px)' : 'translate(0px, 900px)' }}
                    >
                      <div className="rounded-2xl">
                        <img 
                          src={step.image} 
                          width="400" 
                          className="h-[34rem] w-full object-cover" 
                          alt={step.title} 
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="slider-progress-bar-container">
                <div 
                  id="sliderProgressBar" 
                  className="slider-progress-bar" 
                  ref={progressBarRef}
                  style={{ width: `${(activeIndex / (processSteps.length - 1)) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductDevelopmentProcess;